import type { ClusterEvent } from './cluster-event';

// socket.io rooms fanned out by the generic gateway bridge.
export const ROOM_AUTH_UPDATES = 'auth:updates';
export const ROOM_POLICIES_UPDATES = 'policies:updates';
export const ROOM_ORGANIZATIONS_UPDATES = 'organizations:updates';
export const ROOM_ROLES_UPDATES = 'roles:updates';
export const ROOM_APPROVALS_UPDATES = 'approvals:updates';
export const ROOM_SECRETS_UPDATES = 'secrets:updates';
export const ROOM_PROJECTS_UPDATES = 'projects:updates';
export const ROOM_AGENT_FACTORY_UPDATES = 'agent-factory:updates';
export const ROOM_AGENT_RUNTIME_UPDATES = 'agent-runtime:updates';
export const ROOM_REPOSITORIES_UPDATES = 'repositories:updates';
export const ROOM_INDEXER_UPDATES = 'indexer:updates';
export const ROOM_SANDBOXES_UPDATES = 'sandboxes:updates';
export const ROOM_SERVICES_UPDATES = 'services:updates';
export const ROOM_JOBS_UPDATES = 'jobs:updates';
export const ROOM_SETTINGS_UPDATES = 'settings:updates';
export const ROOM_COMMERCE_UPDATES = 'commerce:updates';
export const ROOM_STORE_EDGE_UPDATES = 'store-edge:updates';
export const ROOM_CINEMA_UPDATES = 'cinema:updates';
export const ROOM_VOICE_UPDATES = 'voice:updates';
export const ROOM_MEDIA_UPDATES = 'media:updates';

/** Rooms whose realtime fan-out and joins must be scoped to a verified org. */
export const ORG_SCOPED_REALTIME_ROOMS: readonly string[] = [
  ROOM_COMMERCE_UPDATES,
  ROOM_STORE_EDGE_UPDATES,
  ROOM_CINEMA_UPDATES,
  ROOM_VOICE_UPDATES,
  ROOM_MEDIA_UPDATES,
];

export const ROOM_BY_SOURCE: Readonly<Record<string, string>> = {
  auth: ROOM_AUTH_UPDATES,
  authorization: ROOM_POLICIES_UPDATES,
  organizations: ROOM_ORGANIZATIONS_UPDATES,
  roles: ROOM_ROLES_UPDATES,
  approvals: ROOM_APPROVALS_UPDATES,
  secrets: ROOM_SECRETS_UPDATES,
  projects: ROOM_PROJECTS_UPDATES,
  'agent-factory': ROOM_AGENT_FACTORY_UPDATES,
  'agent-runtime': ROOM_AGENT_RUNTIME_UPDATES,
  'agent-worker': ROOM_AGENT_RUNTIME_UPDATES,
  repositories: ROOM_REPOSITORIES_UPDATES,
  indexer: ROOM_INDEXER_UPDATES,
  daytona: ROOM_SANDBOXES_UPDATES,
  subjects: ROOM_SERVICES_UPDATES,
  core: ROOM_JOBS_UPDATES,
  orders: ROOM_COMMERCE_UPDATES,
  payments: ROOM_COMMERCE_UPDATES,
  // Phase 7 subscriptions (P3-T6): catalog 1.17.0 durable events ride the
  // existing org-scoped commerce room — deliberately NO new room.
  subscriptions: ROOM_COMMERCE_UPDATES,
  'pos-rooms': ROOM_STORE_EDGE_UPDATES,
  ticketing: ROOM_CINEMA_UPDATES,
  'voice-bridge': ROOM_VOICE_UPDATES,
  // Domains that already fan out over their own raw channel from their own
  // dedicated bridge (storage/planning/meetings/voice/communication) are
  // intentionally excluded here to avoid duplicate emission to the same room.
};

// Event-type overrides take precedence over the source map.
export const ROOM_BY_EVENT_TYPE: Readonly<Record<string, string>> = {
  'jobs:updated': ROOM_JOBS_UPDATES,
  'core:feature-flag-toggled': ROOM_SETTINGS_UPDATES,
};

// High-frequency or sensitive durable types that must never be fanned out.
export const FANOUT_EXCLUDED_EVENT_TYPES: ReadonlySet<string> = new Set([
  'authorization:audit-logged',
  'secrets:vault-read',
  'agent:audio-transcript',
  'audit:recorded',
  'payments:webhook-received',
  'orders:order-quoted',
  'media:job-progress',
  // P5-T06 (catalog 1.19.0): playback tokens are minted per playback start AND
  // per refresh — same high-frequency rationale as media:job-progress. Rides
  // the raw Redis `media:events` channel only (WsMediaEventsBridge); excluded
  // so a durable relay never floods `events:fanout`. auditable:false in the
  // catalog (packages/types/src/events/catalog/media.ts).
  'media:playback-token-issued',
]);

export function resolveRoomForEvent(source: string, type: string): string | null {
  if (FANOUT_EXCLUDED_EVENT_TYPES.has(type)) return null;
  return ROOM_BY_EVENT_TYPE[type] ?? ROOM_BY_SOURCE[source] ?? null;
}

export function resolveRoomForClusterEvent(
  event: Pick<ClusterEvent, 'source' | 'type'>,
): string | null {
  return resolveRoomForEvent(event.source, event.type);
}

// Rooms the admin UI subscribes to globally.
export const ALL_REALTIME_ROOMS: readonly string[] = [
  ROOM_AUTH_UPDATES,
  ROOM_POLICIES_UPDATES,
  ROOM_ORGANIZATIONS_UPDATES,
  ROOM_ROLES_UPDATES,
  ROOM_APPROVALS_UPDATES,
  ROOM_SECRETS_UPDATES,
  ROOM_PROJECTS_UPDATES,
  ROOM_AGENT_FACTORY_UPDATES,
  ROOM_AGENT_RUNTIME_UPDATES,
  ROOM_REPOSITORIES_UPDATES,
  ROOM_INDEXER_UPDATES,
  ROOM_SANDBOXES_UPDATES,
  ROOM_SERVICES_UPDATES,
  ROOM_JOBS_UPDATES,
  ROOM_SETTINGS_UPDATES,
  ROOM_COMMERCE_UPDATES,
  ROOM_STORE_EDGE_UPDATES,
  ROOM_CINEMA_UPDATES,
  ROOM_VOICE_UPDATES,
];
